import { ref, computed } from 'vue'

export function usePerformanceMonitoring() {
  const metrics = ref(new Map())
  const timers = ref(new Map())
  const performanceTargets = {
    immediate_access: 5, // ms
    api_response: 50, // ms
    field_access: 1, // ms
    component_render: 16, // ms (60fps)
    data_normalization: 10, // ms
    batch_processing: 100 // ms
  }
  
  const startTiming = (operation) => {
    const timingId = `${operation}_${Date.now()}_${Math.random()}`
    const startTime = performance.now()
    
    timers.value.set(timingId, {
      operation,
      startTime,
      marks: []
    })
    
    performance.mark(`${timingId}_start`)
    return timingId
  }
  
  const endTiming = (timingId) => {
    const timer = timers.value.get(timingId)
    if (!timer) {
      console.warn(`Timer not found: ${timingId}`)
      return null
    }
    
    const endTime = performance.now()
    const duration = endTime - timer.startTime
    
    performance.mark(`${timingId}_end`)
    performance.measure(timingId, `${timingId}_start`, `${timingId}_end`)
    
    recordMetric('timing', timer.operation, duration)
    timers.value.delete(timingId)
    
    return duration
  }
  
  const recordMetric = (category, operation, value) => {
    const key = `${category}_${operation}`
    
    if (!metrics.value.has(key)) {
      metrics.value.set(key, {
        operation,
        category,
        values: [],
        totalSamples: 0,
        average: 0,
        min: Infinity,
        max: 0,
        target: performanceTargets[operation] || null
      })
    }
    
    const metric = metrics.value.get(key)
    metric.values.push(value)
    metric.totalSamples++
    metric.min = Math.min(metric.min, value)
    metric.max = Math.max(metric.max, value)
    metric.average = metric.values.reduce((a, b) => a + b, 0) / metric.values.length
    
    // Keep only last 100 samples
    if (metric.values.length > 100) {
      metric.values = metric.values.slice(-100)
    }
    
    metrics.value.set(key, metric)
  }
  
  const trackFieldAccess = (model, field, accessor) => {
    const startTime = performance.now()
    const result = accessor()
    const duration = performance.now() - startTime
    
    recordMetric('field_access', `${model}_${field}`, duration)
    
    if (duration > performanceTargets.field_access) {
      console.warn(`Slow field access: ${model}.${field} took ${duration}ms`)
    }
    
    return result
  }
  
  const trackApiCall = async (operation, apiCall) => {
    const timingId = startTiming(`api_${operation}`)
    
    try {
      const result = await apiCall()
      const duration = endTiming(timingId)
      
      recordMetric('api_response', operation, duration)
      return result
    } catch (error) {
      endTiming(timingId)
      recordMetric('api_error', operation, 1)
      throw error
    }
  }
  
  const checkPerformanceTargets = () => {
    const results = {}
    
    metrics.value.forEach((metric, key) => {
      if (metric.target !== null) {
        results[metric.operation] = {
          target: metric.target,
          actual: metric.average,
          passing: metric.average <= metric.target,
          samples: metric.totalSamples,
          min: metric.min,
          max: metric.max
        }
      }
    })
    
    return results
  }
  
  const getMetrics = () => {
    return Object.fromEntries(metrics.value)
  }
  
  const clearMetrics = () => {
    metrics.value.clear()
    timers.value.clear()
  }
  
  const generatePerformanceReport = () => {
    const targets = checkPerformanceTargets()
    const passing = Object.values(targets).filter(t => t.passing).length
    const total = Object.keys(targets).length
    
    return {
      summary: {
        passing,
        total,
        percentage: total > 0 ? Math.round((passing / total) * 100) : 0
      },
      targets,
      recommendations: generateRecommendations(targets)
    }
  }
  
  const generateRecommendations = (targets) => {
    const recommendations = []
    
    Object.entries(targets).forEach(([operation, result]) => {
      if (!result.passing) {
        const slowdown = result.actual / result.target
        
        if (operation === 'immediate_access' && slowdown > 2) {
          recommendations.push({
            operation,
            issue: `Immediate access is ${slowdown.toFixed(1)}x slower than target`,
            suggestion: 'Check database indices and query optimization'
          })
        } else if (operation === 'field_access' && slowdown > 5) {
          recommendations.push({
            operation,
            issue: `Field access is ${slowdown.toFixed(1)}x slower than target`,
            suggestion: 'Consider caching or reducing computation in getters'
          })
        }
      }
    })
    
    return recommendations
  }
  
  // Compatibility method for services expecting timer objects
  const startTimer = (operation) => {
    const timingId = startTiming(operation)
    return {
      end: (metadata = {}) => {
        const duration = endTiming(timingId)
        return { duration, operation, metadata }
      },
      operation,
      startTime: performance.now()
    }
  }
  
  // Simple stubs for lifecycle methods (not essential for performance tracking)
  const recordError = (error, context = {}) => {
    recordMetric('errors', context.operation || 'unknown', 1)
  }
  
  const markMounted = () => {
    recordMetric('lifecycle', 'mounted', Date.now())
  }
  
  const markInitialized = () => {
    recordMetric('lifecycle', 'initialized', Date.now())
  }

  return {
    startTiming,
    endTiming,
    recordMetric,
    trackFieldAccess,
    trackApiCall,
    checkPerformanceTargets,
    getMetrics,
    clearMetrics,
    generatePerformanceReport,
    
    // Compatibility methods for existing services
    startTimer,
    recordError,
    markMounted,
    markInitialized
  }
}